import { BaseAction } from "../../../core/actions/BaseAction";
import stopManager from "../../../Lib/Utils/StopManager";
import { convertISO8601ToDuration, convertISO8601ToMinutes, convertTimeToArabic, extractStops, formatDateWithDayDateMonth, formatDayAndMonthByLocale } from "../../../Lib/Utils/validationUtil";
import { TrainTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { ILeg, IOffer, ITrip } from "../../../Orchestrator/Types/Index";
import { IConvertOfferToTrainsActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/ProcessBookItAgainOffer";
import { PriceInfoType } from "../../../Orchestrator/Types/Types/Price/PriceType";
import { TrainType } from "../../../Orchestrator/Types/Types/TrainTypes";


export class ConvertOffersToTrainsAction extends BaseAction<TrainType[], IConvertOfferToTrainsActionParams> {

    override async run(): Promise<TrainType[]> {
        const extractedTrains: TrainType[] = [];
        try {
            const { offersData, type = TrainTypeEnum.outbound } = this.params!
            if (!offersData || offersData.length === 0) return [];
            let idCounter = 1;

            offersData.forEach((offerModel) => {
                const trip: ITrip = offerModel.trip;
                const departureTime = convertTimeToArabic(trip.startTime);
                const arrivalTime = convertTimeToArabic(trip.endTime);
                const duration = convertISO8601ToDuration(trip.duration);
                const durationInMinutes = convertISO8601ToMinutes(trip.duration);
                const stops = extractStops(trip);
                const fromName = stopManager.getStopNameById(trip.origin.stopPlaceRef);
                const toName = stopManager.getStopNameById(trip.destination.stopPlaceRef);
                const trainNumbers = trip.legs[0].timedLeg.service.vehicleNumbers.join(", ");
                const transfers = trip?.transfers || 0;
                const shortDate = trip.startTime || ""
                const priceInfo: PriceInfoType = {
                    standardProducts: [],
                    premiumProducts: [],
                    shuttle: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", descriptionText: '' },
                    standardValue: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", descriptionText: '' },
                    standardPro: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", descriptionText: '' },
                    premium: { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", descriptionText: '' },
                };

                if (offerModel.shuttleOffer) {
                    let minimalPrice = offerModel.shuttleOffer.offerSummary.minimalPrice.amount || 0;
                    const seatsAvailable = 0;

                    const scale = offerModel.shuttleOffer.admissionOfferParts[0].price.scale;
                    const amount = offerModel.shuttleOffer.admissionOfferParts[0].price.amount;
                    minimalPrice = amount / ((100 * scale) / scale);
                    const descriptionText = offerModel.shuttleOffer.products[0]?.descriptiveTexts[0]?.description || "";

                    priceInfo.shuttle = {
                        price: minimalPrice,
                        isSoldOut: !Boolean(offerModel.shuttleOffer.offerId),
                        discount: undefined,
                        seatsLeft: seatsAvailable,
                        offerId: offerModel.shuttleOffer.offerId,
                        descriptionText: descriptionText
                    };
                }

                if (offerModel.standardOffer) {
                    let minimalPrice = offerModel.standardOffer.offerSummary.minimalPrice.amount || 0;
                    const seatsAvailable = offerModel?.standardOffer?.reservationOfferParts[0]?.numericAvailability || 0;

                    const scale = offerModel.standardOffer.admissionOfferParts[0].price.scale;
                    //const amount = offerModel.standardOffer.admissionOfferParts[0].price.amount;
                    const basePrice = (offerModel.standardOffer.admissionOfferParts[0].price.amount) / ((100 * scale) / scale);
                    const amount = offerModel.standardOffer.admissionOfferParts.reduce((total, part) => total + part.price.amount, 0);
                    minimalPrice = amount / ((100 * scale) / scale);
                    const descriptionText = offerModel.standardOffer.products[0]?.descriptiveTexts[0]?.description || "";

                    priceInfo.standardValue = {
                        price: minimalPrice,
                        isSoldOut: !Boolean(offerModel.standardOffer.offerId),
                        discount: undefined,
                        seatsLeft: seatsAvailable,
                        offerId: offerModel.standardOffer.offerId,
                        basePrice: basePrice,
                        descriptionText: descriptionText
                    };
                }

                if (offerModel.standardProOffer) {
                    let minimalPrice = offerModel.standardProOffer.offerSummary.minimalPrice.amount || 0;
                    const seatsAvailable = offerModel?.standardProOffer?.reservationOfferParts[0]?.numericAvailability || 0;

                    const scale = offerModel.standardProOffer.admissionOfferParts[0].price.scale;
                    //const amount = offerModel.standardProOffer.admissionOfferParts[0].price.amount;
                    const basePrice = (offerModel.standardProOffer.admissionOfferParts[0].price.amount) / ((100 * scale) / scale);
                    const amount = offerModel.standardProOffer.admissionOfferParts.reduce((total, part) => total + part.price.amount, 0);
                    minimalPrice = amount / ((100 * scale) / scale);
                    const descriptionText = offerModel.standardProOffer.products[0]?.descriptiveTexts[0]?.description || "";

                    priceInfo.standardPro = {
                        price: minimalPrice,
                        isSoldOut: !Boolean(offerModel.standardProOffer.offerId),
                        discount: undefined,
                        seatsLeft: seatsAvailable,
                        offerId: offerModel.standardProOffer.offerId,
                        basePrice: basePrice,
                        descriptionText: descriptionText
                    };
                }

                if (offerModel.premiumOffer) {
                    let minimalPrice = offerModel.premiumOffer.offerSummary.minimalPrice.amount || 0;
                    const seatsAvailable = offerModel?.premiumOffer?.reservationOfferParts[0]?.numericAvailability || 0;
                    const scale = offerModel.premiumOffer.admissionOfferParts[0].price.scale;
                    // const amount = offerModel.premiumOffer.admissionOfferParts[0].price.amount;
                    const basePrice = (offerModel.premiumOffer.admissionOfferParts[0].price.amount) / ((100 * scale) / scale);
                    const amount = offerModel.premiumOffer.admissionOfferParts.reduce((total, part) => total + part.price.amount, 0);
                    minimalPrice = amount / ((100 * scale) / scale);
                    const descriptionText = offerModel.premiumOffer.products[0]?.descriptiveTexts[0]?.description || "";

                    priceInfo.premium = {
                        price: minimalPrice,
                        isSoldOut: !Boolean(offerModel.premiumOffer.offerId),
                        discount: undefined,
                        seatsLeft: seatsAvailable,
                        offerId: offerModel.premiumOffer.offerId,
                        basePrice: basePrice,
                        descriptionText: descriptionText
                    };
                }                

                const train: TrainType = {
                    id: idCounter++,
                    departureTime,
                    arrivalTime,
                    duration,
                    durationInMinutes,
                    isSoldOut: priceInfo.standardValue.isSoldOut && priceInfo.standardPro.isSoldOut && priceInfo.premium.isSoldOut,
                    stops,
                    trainNumbers,
                    priceInfo,
                    type: type,
                    selectedPriceClass: null,
                    transfers,
                    fromName,
                    toName,
                    shortDate,
                    legs: trip.legs
                };


                extractedTrains.push(train);
            });
        } catch (error) {

        }
        finally {
            return extractedTrains;
        }
    }
}

export const convertOffersToTrainsAction = new ConvertOffersToTrainsAction();
